import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEye, FiCompass, FiClock, FiFlag, FiTrendingUp, FiTarget } = FiIcons;

const Vision = () => {
  const strategicGoals = [
    {
      year: '2025',
      goals: [
        'Reach 10,000 students across Sierra Leone and The Gambia',
        'Expand our educational content to cover all major subjects',
        'Establish 50 community learning hubs in underserved areas'
      ]
    },
    {
      year: '2027',
      goals: [
        'Extend our reach to 3 additional West African countries',
        'Launch a comprehensive teacher training program',
        'Develop advanced leadership certification programs'
      ]
    },
    {
      year: '2030',
      goals: [
        'Impact 100,000 young people across West Africa',
        'Create a self-sustaining educational ecosystem',
        'Establish ILM as the leading youth development organization in the region'
      ]
    }
  ];

  const pillars = [
    {
      icon: FiCompass,
      title: 'Educational Excellence',
      description: 'A world where every young person has access to quality, relevant education that prepares them for future success'
    },
    {
      icon: FiFlag,
      title: 'Leadership Development',
      description: 'Communities led by ethical, skilled leaders who drive positive social change and sustainable development'
    },
    {
      icon: FiTrendingUp,
      title: 'Economic Opportunity',
      description: 'Young people equipped with the skills and confidence to create economic opportunities for themselves and others'
    }
  ];

  return (
    <div className="space-y-8 pb-16 md:pb-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiEye} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Vision</h1>
          <div className="max-w-3xl mx-auto">
            <p className="text-lg md:text-xl text-gray-700 italic font-medium mb-6">
              "We envision a West Africa where every young person is empowered through education to reach their 
              full potential and become a catalyst for positive change in their communities."
            </p>
            <p className="text-gray-600 text-sm md:text-base">
              At ILM Global, we look toward a future where education transcends traditional boundaries, 
              where leadership is cultivated from an early age, and where young people are equipped with 
              the knowledge, skills, and confidence to address the unique challenges facing their communities 
              and the broader world.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Vision Pillars */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">The Pillars of Our Vision</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow"
            >
              <div className="bg-primary-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={pillar.icon} className="w-7 h-7 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 text-center mb-2">{pillar.title}</h3>
              <p className="text-gray-600 text-sm text-center">{pillar.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Strategic Goals */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center">
          <SafeIcon icon={FiTarget} className="w-6 h-6 mr-2 text-primary-600" />
          Strategic Goals
        </h2>
        <div className="space-y-8">
          {strategicGoals.map((period, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="flex flex-col md:flex-row gap-6"
            >
              <div className="md:w-1/4">
                <div className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg p-4 text-white text-center">
                  <SafeIcon icon={FiClock} className="w-8 h-8 mx-auto mb-2" />
                  <h3 className="text-xl font-bold">{period.year}</h3>
                  <p className="text-sm opacity-80">Strategic Milestone</p>
                </div>
              </div>
              <div className="md:w-3/4 bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">By {period.year}, we aim to:</h3>
                <ul className="space-y-2">
                  {period.goals.map((goal, goalIndex) => (
                    <li key={goalIndex} className="flex items-start">
                      <div className="bg-primary-100 p-2 rounded-full mr-3 mt-0.5">
                        <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                      </div>
                      <span className="text-gray-700">{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* The Future We See */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-6 md:p-8 text-white"
      >
        <h2 className="text-2xl font-bold mb-6 text-center">The Future We See</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="bg-white bg-opacity-10 rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">Communities Transformed</h3>
            <p className="text-primary-100 text-sm">
              We see communities across West Africa transformed by a new generation of leaders who are 
              equipped with the knowledge, skills, and values to address local challenges and drive 
              sustainable development.
            </p>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">Education Reimagined</h3>
            <p className="text-primary-100 text-sm">
              We envision an educational approach that transcends traditional models, embracing technology 
              and innovation to create engaging, accessible learning experiences that prepare young people 
              for the challenges of the 21st century.
            </p>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">Leadership Cultivated</h3>
            <p className="text-primary-100 text-sm">
              We see a West Africa where leadership is nurtured from an early age, where young people are 
              empowered to lead with integrity, compassion, and a deep commitment to the common good.
            </p>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">Opportunity Created</h3>
            <p className="text-primary-100 text-sm">
              We envision a future where young people have the tools and confidence to create economic 
              opportunities for themselves and their communities, breaking cycles of poverty and dependency.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Join Us */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100 text-center"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Help Us Realize This Vision</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-6 text-sm md:text-base">
          Our vision is ambitious, but with your support, it's achievable. Join us in creating a future 
          where every young person in West Africa has the opportunity to learn, lead, and thrive.
        </p>
        <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
          <button className="bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors">
            Partner With Us
          </button>
          <button className="border border-primary-500 text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors">
            Learn More About Our Work
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Vision;